import { useEffect } from "react";
import { collectionRef } from "@/api/collectionRef/collectionOperations";
import { useChallengeStore } from "@/store/challenges/challenges.store.v2";
import { Challenge } from "@/models/database/challenges/challenge.model";
import { getAuthorProfileBasedOnUid } from "@/api/search/searchUsernameByName";

export const useTenantChallenges = (tenantId: string, status: string) => {
  const { challenges, loading, error, setChallenges, setLoading, setError } =
    useChallengeStore();

  const fetchChallenges = async () => {
    try {
      setLoading(true);
      const challengesArray: Challenge[] = [];

      const ref = collectionRef.challenge
        .where("client", "==", tenantId)
        .where("status", "==", status);
      const query = await ref.get();

      // Get challenges from Firestore
      await Promise.all(
        query.docs.map(async (doc) => {
          const author = await getAuthorProfileBasedOnUid(
            doc.data().createdBy,
            doc.data().client
          );
          const challengeData = { author: author, id: doc.id, ...doc.data() };
          challengesArray.push(challengeData as Challenge);
          return challengeData;
        })
      );

      const updatedChallenges = [...(challenges || []), ...challengesArray];
      // Remove duplicates based on challenge ID
      const uniqueChallenges = Array.from(
        new Map(
          updatedChallenges.map((challenge) => [challenge.slug, challenge])
        ).values()
      );

      setChallenges(uniqueChallenges);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch challenges
    if (!challenges) {
      fetchChallenges();
    }
  }, []);

  useEffect(() => {
    // Fetch challenges if status changes
    if (status) {
      fetchChallenges();
    }
  }, [status]);

  const filteredByStatus = challenges?.filter(
    (challenge) => challenge.status === status
  );

  const filteredChallenges = filteredByStatus || challenges;

  return {
    data: filteredChallenges,
    loading,
    error,
  };
};
